/**
 * `renderSections(originalHtml, model)`: the original with the model's
 * edits applied. Pure and deterministic.
 *
 * The output is assembled from slices of the original string. Only two
 * things are ever written that are not original bytes: an escaped slot
 * value at a slot's own offsets, and whole rows in another order. An
 * empty model therefore gives back the original byte for byte, through
 * the same code path as any other model.
 *
 * The model is not trusted: every stored value is checked again here, so
 * a model that was edited by hand or saved against an older original
 * cannot inject markup or land on the wrong slot. What cannot be applied
 * is reported in `unmapped` and left out.
 */
import {
  CHUNK_BOTTOM,
  CHUNK_TOP,
  analyseSections,
  frameSections,
  slotProblems,
  type AttrSite,
  type Confidence,
  type Problem,
  type SectionDocument,
  type SectionInfo,
  type SectionKind,
  type Slot,
  type SlotKind,
  type SlotProtection,
  type SlotValues,
} from './analyse.js';
import {
  checkSlotEdit,
  editAsInput,
  findSlotInstance,
  instanceId,
  resolveLayout,
  type ResolvedLayout,
  type SectionEditModel,
  type SectionInstance,
  type SlotEdit,
  type UnmappedEdit,
} from './model.js';
import { escapeAttr, renderTextValue } from './text.js';

export interface RenderResult {
  html: string;
  /** Edits in the model that could not be applied. Empty for a model made by the operations against this original. */
  unmapped: UnmappedEdit[];
}

interface Splice {
  start: number;
  end: number;
  text: string;
}

function attrSplice(site: AttrSite, value: string): Splice {
  // "=" is written as an entity in a description so that words like
  // "online = easy" can never read as an on*-handler to the save check.
  const escaped =
    site.name === 'alt' ? escapeAttr(value).replace(/=/g, '&#61;') : escapeAttr(value);
  const text =
    site.mode === 'quoted'
      ? escaped
      : site.mode === 'unquoted'
        ? `"${escaped}"`
        : site.mode === 'bare'
          ? `="${escaped}"`
          : ` ${site.name}="${escaped}"`;
  return { start: site.start, end: site.end, text };
}

function splicesFor(slot: Slot, edit: SlotEdit): Splice[] {
  const out: Splice[] = [];
  if (slot.kind === 'text') {
    if (edit.text === undefined) return out;
    const text = renderTextValue(edit.text, slot.br);
    out.push({ ...slot.span, text });
    for (const twin of slot.twins) out.push({ ...twin, text });
  } else if (slot.kind === 'image') {
    if (edit.src !== undefined) out.push(attrSplice(slot.srcSite, edit.src));
    if (edit.alt !== undefined) out.push(attrSplice(slot.altSite, edit.alt));
    const src = edit.src ?? (edit.o === 1 ? slot.src : undefined);
    const alt = edit.alt ?? (edit.o === 1 ? (slot.alt ?? undefined) : undefined);
    for (const twin of slot.twins) {
      if (src !== undefined) out.push(attrSplice(twin.srcSite, src));
      if (alt !== undefined && twin.altSite) out.push(attrSplice(twin.altSite, alt));
    }
  } else if (edit.href !== undefined) {
    out.push(attrSplice(slot.hrefSite, edit.href));
    for (const twin of slot.twins) out.push(attrSplice(twin, edit.href));
  }
  return out;
}

/** The stored edits that are still valid for this document, keyed by slot instance id, plus what is not. */
function validEdits(
  doc: SectionDocument,
  model: SectionEditModel,
  resolved: ResolvedLayout,
): { edits: Map<string, SlotEdit>; unmapped: UnmappedEdit[] } {
  const edits = new Map<string, SlotEdit>();
  const unmapped: UnmappedEdit[] = [];
  const stored = model.slots;
  if (stored === null || typeof stored !== 'object') return { edits, unmapped };
  for (const [id, raw] of Object.entries(stored)) {
    const slot = findSlotInstance(doc, resolved, id);
    if (!slot) {
      unmapped.push({
        code: 'slot_missing',
        id,
        message: 'This edit belongs to content that is no longer in the mail.',
      });
      continue;
    }
    const checked =
      raw !== null && typeof raw === 'object'
        ? checkSlotEdit(slot, editAsInput(slot, raw))
        : ({
            ok: false,
            error: { code: 'wrong_slot_kind', message: 'The edit is not readable.' },
          } as const);
    if (!checked.ok) {
      unmapped.push({ code: 'slot_refused', id, message: checked.error.message });
      continue;
    }
    if (Object.keys(checked.edit).length > 0) edits.set(id, checked.edit);
  }
  return { edits, unmapped };
}

/** The name the marketer gave a section, when the model holds a usable one. */
function givenName(model: SectionEditModel, sectionId: string): string | undefined {
  const names: unknown = model.names;
  if (names === null || typeof names !== 'object') return undefined;
  const name = (names as Record<string, unknown>)[sectionId];
  return typeof name === 'string' && name !== '' ? name : undefined;
}

/** Names stored for sections that no longer exist. */
function nameIssues(
  doc: SectionDocument,
  model: SectionEditModel,
  resolved: ResolvedLayout,
): UnmappedEdit[] {
  const names: unknown = model.names;
  if (names === null || typeof names !== 'object') return [];
  return Object.keys(names)
    .filter(
      (id) => !resolved.sections.some((s) => s.id === id) && !doc.sections.some((s) => s.id === id),
    )
    .map((id) => ({
      code: 'name_missing' as const,
      id,
      message: 'A named section is no longer in the mail.',
    }));
}

function slotsByChunk(doc: SectionDocument): Map<number, Slot[]> {
  const map = new Map<number, Slot[]>();
  for (const slot of doc.slots) {
    const list = map.get(slot.chunk);
    if (list) list.push(slot);
    else map.set(slot.chunk, [slot]);
  }
  return map;
}

/** Attribute a preview marks a slot element with (value: the slot instance id). */
export const PREVIEW_SLOT_ATTR = 'data-mf-slot';
/** Attribute a preview marks the first tag of every section row with (value: the section instance id). */
export const PREVIEW_SECTION_ATTR = 'data-mf-section';

/** Where an attribute can be added to the tag that opens at `at`: right after its name. */
function afterTagName(html: string, at: number): number | null {
  const match = /^<[a-zA-Z][a-zA-Z0-9]*/.exec(html.slice(at, at + 32));
  return match ? at + match[0].length : null;
}

/** Offset of the first opening tag in `[start, end)` that is not inside a comment. */
function firstOpenTag(html: string, start: number, end: number): number | null {
  let at = start;
  while (at < end) {
    const lt = html.indexOf('<', at);
    if (lt === -1 || lt >= end) return null;
    if (html.startsWith('<!--', lt)) {
      const close = html.indexOf('-->', lt + 4);
      if (close === -1) return null;
      at = close + 3;
      continue;
    }
    if (/[a-zA-Z]/.test(html.charAt(lt + 1))) return lt;
    at = lt + 1;
  }
  return null;
}

interface RowRange {
  first: number;
  last: number;
}

/** What the rule for the top of the mail does under the layout in force. */
export interface TopSeparators {
  /**
   * The instance rendered first, when its lead separator rows are left
   * out (`dropped`); `null` when the rule leaves every section whole.
   */
  shownFirst: SectionInstance | null;
  /** Lead separator rows of `shownFirst` that are not rendered. */
  dropped: RowRange | null;
  /** Separator rows copied, as original bytes, above every instance of `receiver` that is not rendered first. */
  donor: RowRange | null;
  /** The mail's original first section. */
  receiver: SectionInfo | null;
}

/**
 * Separator rows belong to the section below them. When the mail's first
 * section has none (the usual case), the section that ends up first is
 * rendered without its separator, and the original first section gets
 * one (a copy of the first separator in the mail) when it is no longer
 * first. The renderer and {@link viewSections} both follow this, so the
 * view never lists a slot that the mail leaves out.
 */
export function topSeparators(doc: SectionDocument, resolved: ResolvedLayout): TopSeparators {
  const none: TopSeparators = { shownFirst: null, dropped: null, donor: null, receiver: null };
  const leadOf = (section: SectionInfo): RowRange | null => {
    const head = doc.blocks[section.blocks!.first]!;
    return head.content > head.first ? { first: head.first, last: head.content - 1 } : null;
  };
  const receiver = resolved.sections[0];
  if (!receiver || leadOf(receiver)) return none;
  const donor = resolved.sections.map(leadOf).find((lead) => lead !== null) ?? null;
  if (!donor) return none;
  const shownFirst = resolved.order.find((inst) => !inst.hidden && !inst.removed) ?? null;
  return {
    shownFirst,
    dropped: shownFirst ? leadOf(shownFirst.section) : null,
    donor,
    receiver,
  };
}

/** Renders an already analysed document. `html` must be the string `doc` was computed from. */
export function renderSectionDocument(
  doc: SectionDocument,
  html: string,
  model: SectionEditModel,
): RenderResult {
  return render(doc, html, model, false);
}

/**
 * The same render with markers for an editor canvas: every slot element
 * carries {@link PREVIEW_SLOT_ATTR} (a text slot is wrapped in a `span`
 * for it) and the first tag of every section row carries
 * {@link PREVIEW_SECTION_ATTR}. For display only: this output is never
 * saved or sent, and the Outlook duplicates are not marked.
 */
export function renderSectionPreview(
  doc: SectionDocument,
  html: string,
  model: SectionEditModel,
): RenderResult {
  return render(doc, html, model, true);
}

function render(
  doc: SectionDocument,
  html: string,
  model: SectionEditModel,
  mark: boolean,
): RenderResult {
  if (doc.sourceLength !== html.length) {
    throw new Error('renderSectionDocument: the document was computed from another HTML string.');
  }
  const resolved = resolveLayout(doc, model);
  const { edits, unmapped } = validEdits(doc, model, resolved);
  const byChunk = slotsByChunk(doc);
  const parts: string[] = [];
  // Text before <body> (the title) cannot hold a marker element.
  const bodyAt = mark ? Math.max(0, html.search(/<body\b/i)) : 0;

  const markerFor = (slot: Slot, id: string, edit: SlotEdit | undefined): Splice[] => {
    const attr = ` ${PREVIEW_SLOT_ATTR}="${escapeAttr(id)}"`;
    if (slot.kind === 'text') {
      if (slot.span.start < bodyAt) return [];
      const inner =
        edit?.text !== undefined
          ? renderTextValue(edit.text, slot.br)
          : html.slice(slot.span.start, slot.span.end);
      return [{ ...slot.span, text: `<span${attr}>${inner}</span>` }];
    }
    const at = afterTagName(html, slot.at);
    return at === null ? [] : [{ start: at, end: at, text: attr }];
  };

  const emit = (
    start: number,
    end: number,
    chunk: number,
    dup: number | null,
    section: string | null = null,
  ): void => {
    const splices: Splice[] = [];
    if (mark && section !== null) {
      const tag = firstOpenTag(html, start, end);
      const at = tag === null ? null : afterTagName(html, tag);
      if (at !== null) {
        splices.push({
          start: at,
          end: at,
          text: ` ${PREVIEW_SECTION_ATTR}="${escapeAttr(section)}"`,
        });
      }
    }
    for (const slot of byChunk.get(chunk) ?? []) {
      const id = instanceId(slot.id, dup);
      const edit = edits.get(id);
      if (mark) {
        const markers = markerFor(slot, id, edit);
        splices.push(...markers);
        // A marked text slot already carries its edited text; its Outlook duplicates follow below.
        if (edit && slot.kind === 'text' && markers.length > 0) {
          if (edit.text !== undefined) {
            const text = renderTextValue(edit.text, slot.br);
            for (const twin of slot.twins) splices.push({ ...twin, text });
          }
          continue;
        }
      }
      if (edit) splices.push(...splicesFor(slot, edit));
    }
    if (splices.length === 0) {
      parts.push(html.slice(start, end));
      return;
    }
    splices.sort((a, b) => a.start - b.start || a.end - b.end);
    let at = start;
    for (const splice of splices) {
      /* v8 ignore next -- slots never overlap; a guard against a corrupt document */
      if (splice.start < at || splice.end > end) continue;
      parts.push(html.slice(at, splice.start), splice.text);
      at = splice.end;
    }
    parts.push(html.slice(at, end));
  };
  const emitRows = (first: number, last: number, dup: number | null, section: string): void => {
    for (let r = first; r <= last; r += 1) {
      const row = doc.rows[r]!;
      emit(row.start, row.end, r, dup, section);
    }
  };

  emit(0, doc.rowsStart, CHUNK_TOP, null);

  const top = topSeparators(doc, resolved);
  for (const inst of resolved.order) {
    if (inst.hidden || inst.removed) continue;
    const rows = inst.section.rows!;
    if (inst === top.shownFirst) {
      emitRows(top.dropped ? top.dropped.last + 1 : rows.first, rows.last, inst.dup, inst.id);
      continue;
    }
    if (top.donor && inst.section === top.receiver) {
      // A fixed copy of original bytes: not a slot, never edited or marked.
      for (let r = top.donor.first; r <= top.donor.last; r += 1) {
        parts.push(html.slice(doc.rows[r]!.start, doc.rows[r]!.end));
      }
    }
    emitRows(rows.first, rows.last, inst.dup, inst.id);
  }

  emit(doc.rowsEnd, html.length, CHUNK_BOTTOM, null);

  return {
    html: parts.join(''),
    unmapped: [...resolved.issues, ...unmapped, ...nameIssues(doc, model, resolved)],
  };
}

/** The original with the model applied. An empty model returns the original byte for byte. */
export function renderSections(originalHtml: string, model: SectionEditModel): RenderResult {
  return renderSectionDocument(analyseSections(originalHtml), originalHtml, model);
}

// --- view for a UI -------------------------------------------------------

export interface SlotView {
  /** The id to pass to `setSlot` (differs from `slotId` inside a duplicate). */
  id: string;
  slotId: string;
  kind: SlotKind;
  /** Current values: `text`, or `src` and `alt`, or `href`. */
  values: SlotValues;
  edited: boolean;
  protected: SlotProtection | null;
  mergeFields: string[];
  hidden: boolean;
  /** The slot has a duplicate for Outlook that is written together with it. */
  hasOutlookTwin: boolean;
  problems: Problem[];
}

export interface SectionView {
  /** The id to pass to the section operations. */
  id: string;
  sectionId: string;
  name: string;
  kind: SectionKind;
  confidence: Confidence;
  reason: string;
  rows: { first: number; last: number } | null;
  /** Content rows inside the section where `splitSection` is allowed, as block ids with the row's name. */
  splitPoints: { blockId: string; name: string }[];
  hidden: boolean;
  /** Section id this is a copy of, or `null`. */
  duplicateOf: string | null;
  /** The slots this instance renders (a hidden one: would render). A lead divider the mail leaves out above the first section is not listed. */
  slots: SlotView[];
}

export interface SectionsView {
  /** Top frame, the sections in output order (hidden ones included, removed ones not), bottom frame. */
  sections: SectionView[];
  /** Removed original sections that `restoreSection` can bring back. */
  removed: { id: string; name: string }[];
  /** Problems of the mail as it would render now, each on its slot instance. */
  problems: Problem[];
  unmapped: UnmappedEdit[];
}

/** Everything a UI needs to draw the editor for `model`: sections in order, slots with their current values, and problems in place. */
export function viewSections(doc: SectionDocument, model: SectionEditModel): SectionsView {
  const resolved = resolveLayout(doc, model);
  const { edits, unmapped } = validEdits(doc, model, resolved);
  const byId = new Map(doc.slots.map((s) => [s.id, s]));
  const problems: Problem[] = [];

  const slotView = (slot: Slot, dup: number | null): SlotView => {
    const id = instanceId(slot.id, dup);
    const edit = edits.get(id);
    const values: SlotValues =
      slot.kind === 'text'
        ? { text: edit?.text ?? slot.value }
        : slot.kind === 'image'
          ? {
              src: edit?.src ?? slot.src,
              alt: edit?.alt ?? slot.alt,
              outlookSynced: edit?.src !== undefined || edit?.o === 1,
            }
          : { href: edit?.href ?? slot.href };
    return {
      id,
      slotId: slot.id,
      kind: slot.kind,
      values,
      edited: edit !== undefined,
      protected: slot.protected,
      mergeFields: slot.mergeFields,
      hidden: slot.hidden,
      hasOutlookTwin: slot.twins.length > 0,
      problems: slotProblems(slot, values, id),
    };
  };

  const top = topSeparators(doc, resolved);
  const sectionView = (
    section: SectionInfo,
    dup: number | null,
    hidden: boolean,
    leftOut: RowRange | null = null,
  ): SectionView => {
    const slots = section.slotIds
      .map((slotId) => byId.get(slotId)!)
      .filter((slot) => !leftOut || slot.chunk < leftOut.first || slot.chunk > leftOut.last)
      .map((slot) => slotView(slot, dup));
    if (!hidden) for (const slot of slots) problems.push(...slot.problems);
    const splitPoints: SectionView['splitPoints'] = [];
    if (section.blocks) {
      for (let b = section.blocks.first + 1; b <= section.blocks.last; b += 1) {
        const block = doc.blocks[b]!;
        if (block.canStart) splitPoints.push({ blockId: block.id, name: block.name });
      }
    }
    return {
      id: instanceId(section.id, dup),
      sectionId: section.id,
      name: givenName(model, section.id) ?? section.name,
      kind: section.kind,
      confidence: section.confidence,
      reason: section.reason,
      rows: section.rows,
      splitPoints,
      hidden,
      duplicateOf: dup === null ? null : section.id,
      slots,
    };
  };

  const frames = frameSections(doc);
  const sections: SectionView[] = [];
  if (frames.top) sections.push(sectionView(frames.top, null, false));
  for (const inst of resolved.order) {
    if (inst.removed) continue;
    // The lead separator the renderer leaves out above the first section is not listed either.
    const leftOut = inst === top.shownFirst ? top.dropped : null;
    sections.push(sectionView(inst.section, inst.dup, inst.hidden, leftOut));
  }
  if (frames.bottom) sections.push(sectionView(frames.bottom, null, false));

  problems.push(...doc.problems.filter((p) => p.slotId === undefined));
  return {
    sections,
    removed: resolved.order
      .filter((inst) => inst.removed)
      .map((inst) => ({
        id: inst.id,
        name: givenName(model, inst.section.id) ?? inst.section.name,
      })),
    problems,
    unmapped: [...resolved.issues, ...unmapped, ...nameIssues(doc, model, resolved)],
  };
}
